import {
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { useAuth } from '@spotify/confidence-core-react';

import { useAccounts } from '../../hooks/useAccounts';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  divider: {
    flex: 1,
  },
}));

interface AppRedirectPageProps {
  targetUrl: string;
}

export const AppRedirectPage = ({ targetUrl }: AppRedirectPageProps) => {
  const classes = useStyles();
  const { loginWithPopup, logout, user } = useAuth();
  const {
    accounts,
    switchAccount,
    selectedAccountId,
    selectedAccount,
    isLoading,
  } = useAccounts();

  const otherAccounts = accounts.filter(a => a.accountId !== selectedAccountId);
  const accountLabel =
    selectedAccount?.displayName || selectedAccountId || 'Unknown';

  let switchAccountControl;
  if (isLoading) {
    switchAccountControl = (
      <Button variant="outlined" size="large" fullWidth disabled>
        Loading accounts...
      </Button>
    );
  } else if (otherAccounts.length > 0) {
    switchAccountControl = (
      <TextField
        select
        variant="outlined"
        fullWidth
        value="placeholder"
        onChange={e => switchAccount(e.target.value)}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="placeholder" disabled>
          Switch account
        </MenuItem>
        {otherAccounts.map(account => (
          <MenuItem key={account.accountId} value={account.accountId}>
            {account.displayName || account.accountId}
          </MenuItem>
        ))}
      </TextField>
    );
  } else {
    switchAccountControl = (
      <Button
        variant="outlined"
        color="primary"
        size="large"
        fullWidth
        onClick={() => loginWithPopup(undefined, { timeoutInSeconds: 600 })}
      >
        Log in to another account
      </Button>
    );
  }

  return (
    <div className={classes.container}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        maxWidth={480}
      >
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          This account should be used on another site
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="textSecondary"
          paragraph
        >
          You are signed in as <strong>{user?.email ?? 'unknown'}</strong> on
          account <strong>{accountLabel}</strong>. This account should be used
          on another site.
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={20}
          width="100%"
          mt={2}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            href={targetUrl}
          >
            Continue to {new URL(targetUrl).hostname}
          </Button>
          <Box display="flex" alignItems="center" gridGap={8} width="100%">
            <Divider className={classes.divider} />
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
            <Divider className={classes.divider} />
          </Box>
          {switchAccountControl}
          <Box display="flex" alignItems="center" gridGap={8} width="100%">
            <Divider className={classes.divider} />
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
            <Divider className={classes.divider} />
          </Box>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            Log out
          </Button>
        </Box>
      </Box>
    </div>
  );
};
